import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const DonationsPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="40" width="60" height="60" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 55h40M50 70h40M50 85h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="90" cy="85" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      case 3:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="50" width="60" height="40" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 65h40M50 80h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="45" cy="95" r="6" fill="currentColor"/>
            <circle cx="95" cy="95" r="6" fill="currentColor"/>
          </svg>
        );
      case 4:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="70" cy="50" r="8" fill="currentColor"/>
            <path d="M60 90l20 20 20-20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Strategic Donation Planning & Valuation',
      description: 'We identify ideal donation candidates within your excess stock, align items with verified nonprofit needs for maximum community impact, and determine fair market value using compliant methodologies to support your tax deductions.',
    },
    {
      step: 2,
      title: 'Comprehensive Documentation & Compliance',
      description: 'We manage certified appraisals for high-value items, prepare IRS-compliant Form 8283 with supporting records, ensure chain-of-custody tracking, and verify recipient 501(c)(3) status and proper utilization, guaranteeing audit-ready documentation.',
    },
    {
      step: 3,
      title: 'End-to-End Logistics Management',
      description: 'Our team coordinates pickup and transport from your facilities, manages sorting and processing to prepare items for distribution, oversees delivery to matched nonprofit partners, and facilitates international shipments for global humanitarian efforts if needed.',
    },
    {
      step: 4,
      title: 'Impact Measurement & Reporting',
      description: 'We quantify the community benefit, track end-user impact where feasible, provide ESG-ready reporting metrics (like landfill diversion), and deliver a complete tax documentation package, demonstrating the full value of your corporate giving.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'donation-challenge',
        'commerce-community',
        'beyond-intentions',
        'key-benefits',
        'nonprofit-network'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">Charitable Donations</div>
              <h1 className="sv-title">Charitable Donation Programs</h1>
              <p className="sv-subtitle">Support your community while receiving tax benefits and detailed impact reporting for your non-sellable but usable inventory.</p>
            </section>

            {/* 4-Step Process Carousel */}
            <section className="ti-steps-carousel">
              <div className="ti-carousel-slide-container">
                <div 
                  className="ti-carousel-slides" 
                  style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                  {steps.map((step, index) => (
                    <div key={step.step} className="ti-carousel-slide">
                      <div className="ti-carousel-icon">
                        <StepIcon step={step.step} />
                      </div>
                      <div className="ti-carousel-content">
                        <div className="ti-step-badge">STEP {step.step}</div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ti-carousel-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </section>

            {/* The Charitable Donation Challenge */}
            <section id="donation-challenge" className="sv-overview">
              <h2 className="sv-section-title">The Charitable Donation Challenge</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Donating usable but non-retail-ready inventory effectively requires expert valuation, compliant documentation, seamless logistics, and measurable impact reporting—capabilities many businesses lack internally. Without a structured approach, potential tax advantages are often missed, and the true social benefit isn't fully realized or communicated.
              </p>
            </section>

            {/* Bridging Commerce and Community */}
            <section id="commerce-community" className="sv-overview">
              <h2 className="sv-section-title">Bridging Commerce and Community</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Today's stakeholders expect more than financial performance—they demand measurable social impact. Corporate donation programs offer a powerful bridge between business objectives and community needs, transforming surplus inventory into tangible benefits across multiple dimensions. The most effective programs carefully balance tax efficiency, social impact, and operational practicality.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our data shows that leading companies achieve 32% higher employee engagement scores when their corporate giving programs involve structured inventory donations rather than purely monetary contributions. Meanwhile, consumers increasingly factor corporate giving into purchasing decisions, with 68% expressing stronger brand loyalty toward companies with transparent donation programs that demonstrate direct community impact.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                By transforming what might otherwise become waste into a strategic asset, a well-structured donation program creates value for shareholders, communities, and the environment simultaneously.
              </p>
            </section>

            {/* Beyond Good Intentions */}
            <section id="beyond-intentions" className="sv-overview">
              <h2 className="sv-section-title">Beyond Good Intentions</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Well-intentioned donation programs often falter without specialized expertise. The tax, legal, and logistical complexities create barriers that prevent consistent execution. Common pitfalls include:
              </p>
              <ul className="sv-bullets sv-list" style={{ marginBottom: '24px' }}>
                <li>Improper valuation methodologies leading to unsupportable tax claims</li>
                <li>Missing documentation chains that undermine compliance</li>
                <li>Ineffective matching of goods to nonprofit capabilities</li>
                <li>Transportation bottlenecks that create warehouse disruptions</li>
                <li>Inability to quantify and communicate the actual impact</li>
              </ul>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
                Our structured approach eliminates these barriers through specialized expertise in nonprofit logistics, tax regulation, and impact measurement. By transforming the donation process from an occasional effort into a consistent program, we unlock both financial and reputational benefits that ad-hoc approaches cannot achieve.
              </p>
            </section>

            {/* Key Benefits */}
            <section id="key-benefits" className="sv-features">
              <h2 className="sv-section-title">Key Benefits</h2>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Optimized Tax Benefits</h4>
                  <p>Unlock tax deductions averaging 25–30% of inventory cost through expert valuation and fully compliant documentation.</p>
                </div>
                <div className="sv-feature">
                  <h4>Enhanced Brand Reputation</h4>
                  <p>Demonstrate tangible community support and corporate social responsibility, boosting public relations and stakeholder trust.</p>
                </div>
                <div className="sv-feature">
                  <h4>Measurable Sustainability</h4>
                  <p>Quantify landfill diversion and carbon footprint reductions, providing concrete data for your ESG reporting and goals.</p>
                </div>
                <div className="sv-feature">
                  <h4>Operational Simplicity</h4>
                  <p>Our turnkey program manages the entire process end-to-end, requiring minimal internal resources from your team.</p>
                </div>
                <div className="sv-feature">
                  <h4>Compliance & Risk Mitigation</h4>
                  <p>Protect against improper claims or audit issues with certified appraisals and meticulous, audit-ready records for every donation.</p>
                </div>
              </div>
            </section>

            {/* Our Nonprofit Network */}
            <section id="nonprofit-network" className="sv-overview">
              <h2 className="sv-section-title">Our Nonprofit Network</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                The effectiveness of any donation program ultimately depends on the capacity of recipient organizations to utilize donated goods appropriately. Our carefully cultivated network includes:
              </p>
              <ul className="sv-bullets sv-list" style={{ marginBottom: '24px' }}>
                <li>Disaster relief agencies requiring rapid deployment of essential goods</li>
                <li>Community development organizations serving economically disadvantaged populations</li>
                <li>Educational institutions supporting under-resourced students</li>
                <li>Healthcare charities providing medical and hygiene supplies</li>
                <li>Veterans support groups assisting with housing and career transitions</li>
                <li>Global humanitarian organizations with international distribution capabilities</li>
              </ul>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Each organization in our network undergoes rigorous vetting, including 501(c)(3) verification, operational capacity assessment, and impact measurement capabilities. This ensures your donated inventory reaches legitimate organizations with demonstrated effectiveness in serving those in need.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
                We maintain regular communication with these partners to understand their evolving needs, allowing us to match your available inventory with the organizations best positioned to utilize it effectively. This targeted approach maximizes both the social impact and the operational efficiency of your donation program.
              </p>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Ready to build a donation program?</h3>
                <p>Create community value while managing surplus responsibly and maximizing tax benefits.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Charitable Donation Programs</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'donation-challenge' ? 'active' : ''}
                    onClick={() => scrollToSection('donation-challenge')}
                  >
                    The Charitable Donation Challenge
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'commerce-community' ? 'active' : ''}
                    onClick={() => scrollToSection('commerce-community')}
                  >
                    Bridging Commerce and Community
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'beyond-intentions' ? 'active' : ''}
                    onClick={() => scrollToSection('beyond-intentions')}
                  >
                    Beyond Good Intentions
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'key-benefits' ? 'active' : ''}
                    onClick={() => scrollToSection('key-benefits')}
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'nonprofit-network' ? 'active' : ''}
                    onClick={() => scrollToSection('nonprofit-network')}
                  >
                    Our Nonprofit Network
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default DonationsPage;
